if (!customElements.get('product-form')) {
  // OverDMA: terms step before "Buy now" (snippets/odma-terms-dialog.liquid, Theme settings > OverDMA).
  // The acceptance is saved on the cart in the same format as the EZ Terms app on the cart page,
  // so the order keeps it and "Buy now" asks only once per cart.
  const TERMS_ATTRIBUTE = 'Terms Accepted At';
  // Set while this browser's cart is known to have the acceptance: "Buy now" then reads the cart before
  // deciding. Otherwise the window opens on the press and the cart is read meanwhile (acceptTerms).
  const ACCEPTED_KEY = 'odma-terms-accepted';
  const hasTerms = (cart) => Boolean(cart && cart.attributes && cart.attributes[TERMS_ATTRIBUTE]);
  // keeps ACCEPTED_KEY in step with a cart Shopify returned, and returns that cart
  const noteTerms = (cart) => {
    try {
      if (hasTerms(cart)) localStorage.setItem(ACCEPTED_KEY, '1');
      else localStorage.removeItem(ACCEPTED_KEY);
    } catch (e) {
      // no storage (private mode): the window just opens first
    }
    return cart;
  };
  const termsNoted = () => {
    try {
      return localStorage.getItem(ACCEPTED_KEY) === '1';
    } catch (e) {
      return false;
    }
  };

  // While the window is open on a touch screen the page behind stays put: iOS scrolls it even under
  // overflow: hidden, so the body is pinned and put back on close, as the quiz does (assets/overdma-quiz.js,
  // lockScroll). Returns what puts it back; nothing to do when a layer behind (quick add) pinned it already.
  const pinPage = () => {
    const body = document.body;
    if (!window.matchMedia('(pointer: coarse)').matches || getComputedStyle(body).position === 'fixed') return () => {};
    const y = window.scrollY;
    Object.assign(body.style, { position: 'fixed', top: `${-y}px`, left: '0', right: '0', width: '100%' });
    return () => {
      Object.assign(body.style, { position: '', top: '', left: '', right: '', width: '' });
      const html = document.documentElement;
      const behavior = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      // a programmatic jump (contract C1): the sticky header must not read it as a scroll down
      html.dataset.odmaJump = '1';
      window.scrollTo(0, y);
      html.style.scrollBehavior = behavior;
      requestAnimationFrame(() => requestAnimationFrame(() => delete html.dataset.odmaJump));
    };
  };
  // on wide screens the window grows from (and shrinks back to) the button that opened it: its
  // transform-origin is that button's centre, kept on screen (assets/odma-theme.css). Phones: it rises
  // from the bottom edge instead.
  const anchor = (dialog, trigger) => {
    const box = trigger && trigger.getBoundingClientRect();
    if (!box || !box.width || window.matchMedia('(max-width: 749px)').matches) {
      dialog.style.removeProperty('transform-origin');
      return;
    }
    const view = document.documentElement;
    const x = Math.min(Math.max(box.left + box.width / 2, 0), view.clientWidth) - (view.clientWidth - dialog.offsetWidth) / 2;
    const y = Math.min(Math.max(box.top + box.height / 2, 0), view.clientHeight) - (view.clientHeight - dialog.offsetHeight) / 2;
    dialog.style.transformOrigin = `${Math.round(x)}px ${Math.round(y)}px`;
  };
  // a button showing that something is going on: Dawn's spinner in place of the label, presses ignored
  const busy = (button, on) => {
    button.classList.toggle('loading', on);
    if (on) button.setAttribute('aria-disabled', 'true');
    else button.removeAttribute('aria-disabled');
    const spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !on);
  };

  // Once the product is being added (or checkout is opening), Cancel, Esc and the backdrop no longer close
  // the window: closing then could leave a unit in the cart or race the checkout navigation. Until then
  // (while the acceptance is saved) they still stop the purchase. stopBuyNow closes it either way.
  const commitTerms = (dialog) => {
    dialog.dataset.committed = 'true';
    dialog.querySelector('[data-terms-cancel]')?.setAttribute('aria-disabled', 'true');
  };

  let answerTerms = null; // settles the question the window is asking now (askTerms)
  let termsOpenedAt = 0;

  // Shows the terms window for one "Buy now". Resolves 'accept' when the customer ticks the box and
  // confirms: the window then stays open, "Check out" loading, while the purchase goes on (ProductForm
  // closes it if checkout does not open). Resolves 'close' when they close it, and 'skip' when the promise
  // `skip` gives a cart first (it has the acceptance already): the window closes and the purchase goes on.
  const askTerms = (dialog, trigger, skip) =>
    new Promise((resolve) => {
      if (typeof dialog.showModal !== 'function') {
        resolve(window.confirm(dialog.querySelector('.odma-terms__text').textContent.replace(/\s+/g, ' ').trim()) ? 'accept' : 'close');
        return;
      }
      // one at a time: a second "Buy now" pressed before the window opened (two cards in a row) just gets
      // its button back, instead of sharing (and resetting) the window the customer is looking at
      if (dialog.open) {
        resolve('close');
        return;
      }
      const form = dialog.querySelector('form');
      const box = form.elements.accept;
      const warning = dialog.querySelector('.odma-terms__alert');
      const checkout = form.querySelector('[type="submit"]');
      const cancel = dialog.querySelector('[data-terms-cancel]');
      const committed = () => dialog.dataset.committed === 'true';
      if (!dialog.dataset.ready) {
        dialog.dataset.ready = 'true';
        // the policies open in a new tab, so the purchase is still here when the customer comes back
        dialog.querySelectorAll('a[href]').forEach((link) => {
          link.target = '_blank';
          link.rel = 'noopener';
        });
        form.addEventListener('submit', (event) => {
          // never closes the window (method="dialog" would): "Check out" shows the purchase going on
          event.preventDefault();
          if (checkout.getAttribute('aria-disabled') === 'true') return;
          if (!box.checked) {
            warning.hidden = false;
            box.setAttribute('aria-invalid', 'true');
            box.focus();
            return;
          }
          busy(checkout, true);
          if (answerTerms) answerTerms('accept');
        });
        box.addEventListener('change', () => {
          if (!box.checked) return;
          warning.hidden = true;
          box.removeAttribute('aria-invalid');
        });
        cancel.addEventListener('click', () => {
          if (!committed()) dialog.close();
        });
        // a click on the backdrop closes it too (not one on its scrollbar, nor the end of a text
        // selection that started inside, nor the second tap of a double tap on "Buy now")
        const outside = (event) => {
          const rect = dialog.getBoundingClientRect();
          return (
            event.target === dialog &&
            (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)
          );
        };
        let pressedOutside = false;
        dialog.addEventListener('pointerdown', (event) => {
          pressedOutside = outside(event) && performance.now() - termsOpenedAt > 400;
        });
        dialog.addEventListener('click', (event) => {
          if (pressedOutside && outside(event) && !committed()) dialog.close();
        });
        // Esc closes it on keydown; the keyup would then reach the quick-add window behind it and close that too
        dialog.addEventListener('cancel', (event) => {
          if (committed()) {
            event.preventDefault();
            return;
          }
          const stop = (event) => {
            if (event.key === 'Escape') event.stopPropagation();
          };
          window.addEventListener('keyup', stop, { capture: true, once: true });
          setTimeout(() => window.removeEventListener('keyup', stop, { capture: true }), 1000);
        });
      }
      form.reset();
      warning.hidden = true;
      box.removeAttribute('aria-invalid');
      busy(checkout, false);
      delete dialog.dataset.committed;
      cancel.removeAttribute('aria-disabled');
      let settled = false;
      const settle = (answer) => {
        if (settled) return;
        settled = true;
        if (answerTerms === settle) answerTerms = null;
        resolve(answer);
      };
      answerTerms = settle;
      dialog.addEventListener('close', () => settle('close'), { once: true });
      if (skip)
        skip.then((cart) => {
          if (!cart || settled) return;
          settle('skip');
          dialog.close();
        });
      dialog.showModal();
      // pinned once the window is up, so a showModal() that fails cannot leave the page pinned
      const unpin = pinPage();
      dialog.addEventListener('close', unpin, { once: true });
      anchor(dialog, trigger);
      termsOpenedAt = performance.now();
    });

  // resolves with the cart as Shopify returns it after saving
  const saveTerms = (dialog) => {
    const zone = dialog.dataset.timeZone;
    const now = new Date();
    let stamp = now.toString();
    if (zone) {
      try {
        stamp = `${now.toLocaleString('en-US', { timeZone: zone })} ${zone}`;
      } catch (e) {
        // unknown time zone: keep the local time
      }
    }
    return fetch(`${routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({ attributes: { [TERMS_ATTRIBUTE]: stamp } }),
    }).then((response) => {
      if (!response.ok) throw new Error(`The terms were not saved (${response.status})`);
      return response.json();
    });
  };

  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        // OverDMA: "Buy now" (snippets/buy-buttons.liquid, odma-buy-now.liquid) goes to checkout, see buyNow()
        this.buyNowButton = this.querySelector('[data-buy-now]');

        // a card's form only has "Buy now", which opens checkout instead of the cart drawer
        if (document.querySelector('cart-drawer') && this.submitButton !== this.buyNowButton)
          this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        if (this.buyNowButton) {
          // back from checkout, the browser can restore this page with "Buy now" still loading
          // (the listener and the cart subscription are added in connectedCallback, see there)
          this.onPageShow = (event) => {
            if (event.persisted) this.stopBuyNow();
          };
          // the press starts reading the cart (readCart): the click comes about 0.1 s later
          this.buyNowButton.addEventListener(
            'pointerdown',
            (event) => {
              const button = this.buyNowButton;
              if (event.button === 0 && !button.disabled && button.getAttribute('aria-disabled') !== 'true') this.readCart();
            },
            { passive: true }
          );
        }
      }

      // Added on every connection, not once in the constructor: the hardware catalog moves its cards
      // (assets/odma-catalog.js re-appends them to sort and page), which disconnects and reconnects
      // their forms, and disconnectedCallback removes both.
      connectedCallback() {
        if (!this.buyNowButton) return;
        window.addEventListener('pageshow', this.onPageShow);
        // any add or change to the cart makes a cart read taken before it stale (and one taken while
        // disconnected may have missed a change)
        this.cartRead = null;
        if (!this.cartUpdateUnsubscriber)
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => (this.cartRead = null));
      }

      disconnectedCallback() {
        if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        this.cartUpdateUnsubscriber = null;
        // A move within the page (the hardware catalog re-appending its cards) reconnects the form at once,
        // so the timer that gives "Buy now" back if checkout does not open must survive it: only a form
        // really taken out of the page drops it.
        queueMicrotask(() => {
          if (!this.isConnected) clearTimeout(this.buyNowTimer);
        });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.buyNowButton?.getAttribute('aria-disabled') === 'true') return;
        if (this.buyNowButton && evt.submitter === this.buyNowButton) return this.buyNow();
        this.cartRead = null;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');
        // OverDMA: one cart request at a time; "Buy now" waits until this add is done (then the
        // variant is in the cart and buyNow() does not add it again)
        this.buyNowButton?.setAttribute('aria-disabled', true);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            this.handleErrorMessage(window.cartStrings.error);
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            if (this.buyNowButton !== this.submitButton) this.buyNowButton?.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      // OverDMA: "Buy now" asks for the terms (acceptTerms), adds the selected variant unless the cart
      // already has it (a second press, or back from checkout, must not add another unit), then opens
      // checkout. With the terms step off, the EZ Terms app can gate it like Shopify's "Buy it now"
      // when its "Buy it now" option is on (tac-bin-override in the snippets).
      buyNow() {
        const button = this.buyNowButton;
        const formData = new FormData(this.form);
        const variantId = Number(formData.get('id'));
        // this purchase; stopBuyNow ends it, and a step still on its way then does nothing
        const flow = (this.flow = {});
        this.handleErrorMessage();
        button.setAttribute('aria-disabled', true);

        this.acceptTerms(this.readCart(true), flow)
          .then((cart) => {
            // the customer closed the terms window (also while the acceptance was being saved)
            if (!cart || this.flow !== flow || (flow.terms && flow.terms.closed)) return null;
            if (flow.terms) commitTerms(flow.terms.dialog);
            if (cart.items && cart.items.some((item) => item.variant_id === variantId)) return {};
            const config = fetchConfig('javascript');
            config.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete config.headers['Content-Type'];
            config.body = formData;
            return fetch(`${routes.cart_add_url}`, config).then((response) => response.json());
          })
          .then((response) => {
            if (this.flow !== flow) return;
            // the terms window was closed, also while the purchase was going on: no checkout then
            if (!response || (flow.terms && flow.terms.closed)) return this.stopBuyNow();
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.stopBuyNow();
              return;
            }
            // data-buy-now holds the checkout URL; the button's own value is where the form goes without
            // JavaScript (the cart while the terms step is on, see snippets/buy-buttons.liquid)
            window.location.href = button.dataset.buyNow || button.value;
            // keep it loading while checkout opens; if that is cancelled, give the button back
            this.buyNowTimer = setTimeout(() => this.stopBuyNow(), 8000);
          })
          .catch((e) => {
            if (this.flow !== flow) return;
            this.handleErrorMessage(window.cartStrings.error);
            console.error(e);
            this.stopBuyNow();
          });
      }

      // OverDMA: resolves with the cart "Buy now" goes on with, or null when the customer closes the
      // window. With the terms step off (no window on the page) or the acceptance already in the cart
      // (from this step or from the EZ Terms app on the cart page), that is the cart read (`reading`).
      // After accepting, it is the cart Shopify returns with the acceptance saved, so a unit added
      // meanwhile (in another tab, while the window was open) is not added again; the window then stays
      // open, showing the purchase going on, until checkout opens (stopBuyNow closes it otherwise).
      acceptTerms(reading, flow) {
        const dialog = document.getElementById('OdmaTermsDialog');
        const ask = (skip) => {
          // no spinner behind the window while the customer reads
          this.showBuyNowSpinner(false);
          // the window is this purchase's (one at a time: an open one belongs to another purchase)
          if (!dialog.open) flow.asking = dialog;
          return askTerms(dialog, this.buyNowButton, skip).then((answer) => {
            // the question is over: the window is no longer this purchase's to close (stopBuyNow), unless
            // it accepted and keeps it open (flow.terms below). Otherwise a purchase cancelled while its
            // acceptance was saved would close the window another "Buy now" opened meanwhile.
            flow.asking = null;
            if (answer === 'skip') {
              this.showBuyNowSpinner(true);
              return skip;
            }
            if (answer !== 'accept') return null;
            const terms = { dialog, closed: false };
            flow.terms = terms;
            dialog.addEventListener('close', () => (terms.closed = true), { once: true });
            return saveTerms(dialog).then(noteTerms);
          });
        };
        if (!dialog || termsNoted()) {
          this.showBuyNowSpinner(true);
          return reading.then((cart) => (!dialog || hasTerms(noteTerms(cart)) ? cart : ask()));
        }
        // the window opens on the press; the cart read meanwhile closes it again when it has the
        // acceptance after all (given on the cart page, or in another browser tab)
        return ask(reading.then((cart) => (hasTerms(cart) ? noteTerms(cart) : null), () => null));
      }

      // GET /cart.js. The press on "Buy now" starts it (pointerdown), so the click about 0.1 s later often
      // finds the cart read already; a click takes it (take). One older than 2 s (a press that did not
      // end in a click) is not used.
      readCart(take = false) {
        let read = this.cartRead;
        if (!read || Date.now() - read.at > 2000) {
          read = {
            at: Date.now(),
            cart: fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((response) => response.json()),
          };
          // a read nobody takes must not report an error; the click that takes one handles it
          read.cart.catch(() => {});
        }
        this.cartRead = take ? null : read;
        return read.cart;
      }

      showBuyNowSpinner(show) {
        this.buyNowButton.classList.toggle('loading', show);
        this.buyNowButton.querySelector('.loading__spinner').classList.toggle('hidden', !show);
      }

      stopBuyNow() {
        if (!this.buyNowButton) return;
        clearTimeout(this.buyNowTimer);
        // the purchase ends here; the terms window it kept open closes with it
        const flow = this.flow;
        const terms = flow && flow.terms;
        this.flow = null;
        if (terms && !terms.closed && terms.dialog.open) terms.dialog.close();
        else if (flow && flow.asking && flow.asking.open) flow.asking.close();
        this.showBuyNowSpinner(false);
        this.buyNowButton.removeAttribute('aria-disabled');
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
        // OverDMA: "Buy now" follows "Add to cart" (sold out, unavailable, loading a variant)
        this.buyNowButton?.toggleAttribute('disabled', disable);
        // "Sold out" shows in red (assets/odma-theme.css); while a variant loads (no text) it keeps its state
        if (!disable || text) this.submitButton.classList.toggle('is-sold-out', disable && text === window.variantStrings.soldOut);
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
